import {Form as FormikForm, Formik} from 'formik'
import moment from 'moment'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import useListData from '../../../api/hooks/useListData'
import useMutate from '../../../api/hooks/useMutate'
import useRequest from '../../../api/hooks/useRequest'
import useSearchRequest from '../../../api/hooks/useSearchRequest'
import ComponentButton from '../../components/ComponentButton'
import ComponentCheckbox from '../../components/ComponentCheckbox'
import ComponentFilters from '../../components/ComponentFilters'
import ComponentSearch from '../../components/ComponentSearch'
import {useFilter, useSearch} from '../helpers'
import {useIntl} from 'react-intl'
import api, {fileApi} from '../../../api'
import SkeletonRows from './src/SkeletonRows'
import useLoading from '../../components/helpers/hooks/useLoading'
import {getErrorToast} from '../../helpers/toasts'
import {ModuleContext} from '../helpers/ModuleContext'
import useUpdate from '../../../api/hooks/useUpdate'
import usePrevious from '../../helpers/usePrevious'
import isEqual from 'lodash/isEqual'
import {useRefetchSubscribers, useSubscribeOnRefetch} from '../helpers/PageContext'
import useInfiniteListData from '../../../api/hooks/useInfiniteListData'
import ComponentModal from '../../components/ComponentModal'
import {TindividualPage, TModuleList, TModuleListUpdateField} from './_types'
import {Pagination} from './src/Pagination'
import {InfiniteScroll} from './src/InfiniteScroll'
import {UpdateFieldsModal} from './src/UpdateFieldsModal'
import valuesCleaner from '../../helpers/valuesCleaner'
import {useShiftDragScroll} from '../../helpers/useShiftDragScroll'
import {useThemeVersion} from '../../helpers/ThemeContext'
import {HeaderCell} from './src/HeaderCell'
import {ListRow} from './src/ListRow'
import {DownloaderModule} from './src/DownloaderModule'
import useStickyHScroll from './hooks/useStickyHScroll'
import {useSearchHistory} from '../../components/ComponentSearch/hook/useSearchHistory'
import {useMediaQuery} from '../../helpers/hooks/useMediaQuery'
import {ListCard} from './src/ListCard'
import SkeletonCards from './src/SkeletonCards'

const filterExcludes = ['limit', 'page', 'sort_by', 'sort_order']

const ModuleList = React.memo<TModuleList>((props) => {
    const intl = useIntl()
    const {version} = useThemeVersion()
    const isNewUI = useMemo(() => version === 'v8', [version])
    const isMobile = useMediaQuery('(max-width: 768px)')
    const {settings, components, hook} = props
    const {
        headers,
        filters: initialFilters,
        is_csv,
        is_exel,
        is_edit = false,
        context: initialContext,
        linked_filter,
        link,
        is_infinite,
        scrollType,
        limit,
    } = settings
    const haveButtons = Boolean(components?.buttons)
    const showCSVDownloader = Boolean(is_csv)
    const showExelDownloader = Boolean(is_exel)
    const isListEditable = is_edit
    const currentLink = typeof link === 'string' ? link : link === false ? null : settings.object
    const withInfiniteScroll = is_infinite
    const isListHiddenWhileEmpty = settings.hide_empty === true
    const tableRef = useShiftDragScroll<HTMLDivElement>()
    const fakeBarRef = useRef<HTMLDivElement | null>(null)
    const isSticky = scrollType === 'sticky'
    const isFirstRender = useRef(true)
    // const { events } = useDraggable(tableRef, {isMounted: !!tableRef.current});

    //блок фильтров
    const haveFilter = Boolean(components?.filters)

    const defaultLimitValues = useMemo(() => [20, 40, 60, 80, 100, 200], [])

    const resolvedLimitValues = useMemo(() => {
        const customLimitValues: number[] = []

        if (Array.isArray(limit)) {
            for (const limitValue of limit) {
                const parsedLimitValue = Number(limitValue)
                if (Number.isFinite(parsedLimitValue) && parsedLimitValue > 0) {
                    customLimitValues.push(parsedLimitValue)
                }
            }
        }

        return Array.from(new Set([...defaultLimitValues, ...customLimitValues])).sort(
            (a, b) => a - b
        )
    }, [limit, defaultLimitValues])

    const defaultLimitValue = useMemo(() => {
        return resolvedLimitValues?.[0] ?? 20
    }, [resolvedLimitValues])

    const resolvedInitialFilterValues = Object.assign(
        {},
        {limit: defaultLimitValue, page: 1},
        initialFilters
    )
    const {filter, isInitials, setFilter, resetFilter} = useFilter(
        `${props.type}_${settings.object}`,
        resolvedInitialFilterValues,
        linked_filter,
        filterExcludes
    )

    /*
    --- Сбрасывать пагинацию при изменении фильтрации
    */
    const previousFilter = usePrevious(filter)

    useEffect(() => {
        if (previousFilter) {
            const previousFilterClone = {...previousFilter}
            const currentFilterClone = {...filter}
            delete previousFilterClone['page']
            delete currentFilterClone['page']
            if (!isEqual(previousFilterClone, currentFilterClone)) {
                setFilter({...currentFilterClone, page: 1})
            }
        }
    }, [filter])

    const context = Object.assign({}, {block: 'list'}, initialContext ?? {})
    const resolvedFilter = {context, ...filter}
    const {
        data: response,
        isLoading: loading,
        isFetching,
        isRefetching,
        refetch,
    } = useListData(settings.object, valuesCleaner(resolvedFilter), !withInfiniteScroll)
    const {
        data: infiniteResponse,
        isLoading: isInifiniteDataLoading,
        isFetching: isInfiniteDataFetching,
        hasNextPage,
        fetchNextPage,
        refetch: infiniteRefetch,
    } = useInfiniteListData(settings.object, valuesCleaner(resolvedFilter), withInfiniteScroll)

    useSubscribeOnRefetch(refetch, setFilter, linked_filter)

    useRefetchSubscribers(
        `${props.type}_${settings.object}`,
        isRefetching,
        Boolean(linked_filter),
        filter,
        filterExcludes
    )

    //проверка обновления при наличии хука
    useUpdate({
        functions: [{active: Boolean(hook), update: refetch}],
        table_name: hook,
        refetchInterval: 2000,
    })

    //блок поиска
    const haveSearch = Boolean(components.search)
    const {search, setSearch} = useSearch(`${props.type}_${settings.object}`)
    const { add } = useSearchHistory(`${props.type}_${settings.object}`)
    const isSetSearchData = haveSearch && Boolean(search.length)
    const {
        isLoading: searchLoading,
        isFetching: isSearchFetching,
        data: searchResponse,
        refetch: searchRefetch,
    } = useSearchRequest({
        object: settings.object,
        context: 'list',
        value: search,
        enabled: haveSearch,
    })

    useEffect(() => {
        if (!search) return
        if (search.length < 2) return
        if (!searchResponse?.data) return
        const hasResults = searchResponse.data.length > 0

        if (!hasResults) return

        add(search)
    }, [searchResponse])

    //запрос полей под массовое редактирование
    const {isLoading: isGroupUpdateFieldsLoading, data: groupUpdateFields} = useRequest(
        'admin',
        'group-update-fields',
        {scheme_name: settings.object},
        is_edit
    )
    const [showUpdateFieldsModal, setShowUpdateFieldsModal] = useState(false)

    const {mutate: deleteItems, isSuccess: successDelete} = useMutate<{
        id: Array<number> | number
    }>(settings.object, 'remove')
    const {mutateAsync: updateItem, isSuccess: successUpdate} = useMutate<{
        id: Array<number> | number
    }>(settings.object, 'update')
    //преобразования
    const filterKeys = useMemo(() => {
        if (haveFilter) {
            const keys: string[] = []
            components.filters.forEach((filter) => {
                if (filter.type === 'date_range') {
                    // Для date_range собираем все recipient_property из fields
                    filter.settings.fields?.forEach((field) => {
                        keys.push(field.recipient_property)
                    })
                } else if ('recipient_property' in filter.settings) {
                    keys.push(filter.settings.recipient_property)
                }
            })
            return keys
        } else {
            return []
        }
    }, [])

    //подключение нужных данных
    const data = isSetSearchData
        ? searchResponse?.data
        : withInfiniteScroll
        ? infiniteResponse?.pages.reduce<
              Array<{
                  [key: string]: any
              }>
          >((acc, currentValue) => acc.concat(currentValue.data), [])
        : response?.data
    const detail = isSetSearchData
        ? searchResponse?.detail
        : withInfiniteScroll
        ? infiniteResponse?.pages[0]?.detail
        : response?.detail
    const isDataLoading = isSetSearchData
        ? searchLoading
        : withInfiniteScroll
        ? isInifiniteDataLoading
        : loading
    const isDataFetching = isSetSearchData
        ? isSearchFetching
        : withInfiniteScroll
        ? false
        : isFetching
    const isEmptyData = !isDataLoading && data?.length === 0
    const dataWithIds = useMemo(() => {
        if (Array.isArray(data)) {
            return data.filter((item) => item.id)
        } else {
            return []
        }
    }, [data])

    useEffect(() => {
        if (successDelete) {
            isSetSearchData ? searchRefetch() : withInfiniteScroll ? infiniteRefetch() : refetch()
        }
    }, [successDelete])

    const handleDelete = (selectedItems: Array<any>) => {
        const selectedItemsIds = selectedItems.map((item) => item.id)
        deleteItems({
            id: selectedItemsIds,
        })
    }

    const handleUpdate = async (selectedItems: Array<any>, initialItems: Array<any>) => {
        const resolvedChangedItems = selectedItems
            .map((item) => {
                const changedValues: any = {}
                const currentItemInitials = initialItems.find(
                    (initialItem) => initialItem.id === item.id
                )
                for (let key in item) {
                    if (item[key] !== currentItemInitials[key] || key === 'id') {
                        changedValues[key] = item[key]
                    }
                }
                return Object.keys(changedValues).length > 1 ? changedValues : null
            })
            .filter((item) => item)

        await Promise.all(resolvedChangedItems.map((item) => updateItem(item)))
        isSetSearchData ? searchRefetch() : refetch()
    }

    const isLoaded = useLoading(isDataFetching)

    const contextValue = useMemo(
        () => ({
            refresh: isSetSearchData
                ? searchRefetch
                : withInfiniteScroll
                ? infiniteRefetch
                : refetch,
        }),
        [isSetSearchData]
    )

    const handleDownloadCSV = async (selectedColumns: Array<string>) => {
        const loadedCSV = await api(settings.object, 'get', {
            context: {block: 'csv'},
            select: selectedColumns,
            ...filter,
        })
        if (loadedCSV) {
            const link = document.createElement('a')
            //@ts-ignore
            link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(loadedCSV)}`
            const filename = `${settings.object} (${moment().format('DD.MM.YY')}).csv`
            link.setAttribute('download', `${filename}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
        } else {
            getErrorToast(intl.formatMessage({id: 'LIST.DOWNLOADER_FILE_ERROR'}))
        }
    }

    const handleDownloadExcel = async (selectedColumns: Array<string>, exportAll?: boolean) => {
        const loadedExcel = await fileApi(settings.object, 'get', {
            context: {block: 'exel'},
            select: selectedColumns,
            ...filter,
            ...(exportAll ? {limit: 0, page: 1} : {}),
        })
        if (loadedExcel) {
            const link = document.createElement('a')
            //@ts-ignore
            link.href = URL.createObjectURL(new Blob([loadedExcel]))
            const filename = `${settings.object} (${moment().format('DD.MM.YY')}).xlsx`
            link.setAttribute('download', `${filename}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
        } else {
            getErrorToast(intl.formatMessage({id: 'LIST.DOWNLOADER_FILE_ERROR'}))
        }
    }

    useStickyHScroll(isSticky, tableRef, fakeBarRef, [headers?.length, isLoaded])

    const doShowActionsCol = useMemo(() => {
        if (!Array.isArray(data)) return false
        // колонки добавляем только если у какой-то строки действительно есть кнопки
        return data.some((row) => {
            const btns = row?.buttons
            if (!btns) return false
            // поддержка форматов: массив кнопок или объект с кнопками
            if (Array.isArray(btns)) return btns.length > 0
            if (typeof btns === 'object') return Object.keys(btns).length > 0
            return false
        })
    }, [data])

    const resolvedHeaders = useMemo(
        () =>
            Array.isArray(headers)
                ? doShowActionsCol
                    ? [...headers, {title: '', article: 'buttons', type: 'buttons'}]
                    : headers
                : [],
        [headers, doShowActionsCol]
    )

    useEffect(() => {
        /**
         * При первом открытии страницы не скроллим
         */
        if (isFirstRender.current) {
            isFirstRender.current = false

            return
        }

        /**
         * Скроллим только после смены страницы
         */
        if (tableRef.current) {
            const rect = tableRef.current.getBoundingClientRect()
            const absoluteTop = window.scrollY + rect.top - 100

            window.scrollTo({
                top: absoluteTop,
                behavior: 'smooth',
            })
        }
    }, [filter.page])

    // класс для включения sticky-стилей только когда колонка действительно есть
    const hasActions = doShowActionsCol

    //модальное окно отрытия отдельной от всей строки ссылки
    const [individualPage, setIndividualPage] = useState<TindividualPage>(null)
    const isModuleHaveIndividualModal = resolvedHeaders.some(
        (header) => header.type === 'link_list'
    )

    if (isListHiddenWhileEmpty && (isDataLoading || data?.length === 0)) {
        return null
    }

    return (
        <ModuleContext.Provider value={contextValue}>
            {isModuleHaveIndividualModal ? (
                <ComponentModal
                    show={individualPage?.context ?? null}
                    page={individualPage?.href}
                    setShow={() => setIndividualPage(null)}
                />
            ) : null}
            {haveFilter ? (
                <ComponentFilters
                    type='string'
                    data={components.filters}
                    filterValues={filter}
                    isInitials={isInitials}
                    handleChange={setFilter}
                    handleReset={resetFilter}
                />
            ) : null}
            <div
                className={`card moduleList ${isSticky ? 'is-sticky' : ''} 
                ${isNewUI ? 'moduleList-v8' : 'moduleList-v7'}
                ${hasActions ? 'has-actions' : ''}
                `}
            >
                <div className='card-body py-3'>
                    {haveSearch || haveButtons || showCSVDownloader || showExelDownloader ? (
                        <div className='moduleList_toolbar'>
                            {haveSearch ? (
                                <ComponentSearch value={search} setValue={setSearch} storageKey={`${props.type}_${settings.object}`} />
                            ) : null}
                            {haveButtons
                                ? components.buttons.map((button) => (
                                      <ComponentButton
                                          key={button.settings.title}
                                          className='primary-light'
                                          {...button}
                                      />
                                  ))
                                : null}
                            {showCSVDownloader ? (
                                <DownloaderModule
                                    title={intl.formatMessage({id: 'LIST.CSV_BUTTON_TITLE'})}
                                    handleDownload={handleDownloadCSV}
                                    columns={headers}
                                />
                            ) : null}
                            {showExelDownloader ? (
                                <DownloaderModule
                                    title={intl.formatMessage({id: 'LIST.EXCEL_BUTTON_TITLE'})}
                                    handleDownload={handleDownloadExcel}
                                    columns={headers}
                                />
                            ) : null}
                        </div>
                    ) : null}
                    <Formik
                        enableReinitialize
                        initialValues={{
                            selectedItems: [] as Array<any>,
                            data: data,
                            mode: 'delete',
                        }}
                        onSubmit={(values, {resetForm}) => {
                            values.mode === 'edit'
                                ? handleUpdate(values.selectedItems, values.data as Array<any>)
                                : handleDelete(values.selectedItems)
                            resetForm()
                        }}
                    >
                        {({values, setFieldValue, handleSubmit}) => {
                            const {selectedItems} = values
                            const isMassCheckboxChecked =
                                Boolean(dataWithIds.length) &&
                                dataWithIds.every((item) =>
                                    selectedItems.find(
                                        (selectedItem) => item.id === selectedItem.id
                                    )
                                )
                            const handleMassCheckboxClick = () => {
                                if (!dataWithIds.length) {
                                    return
                                }

                                const currentItemsToCheck = isMassCheckboxChecked ? [] : dataWithIds
                                return setFieldValue('selectedItems', currentItemsToCheck)
                            }
                            return (
                                <FormikForm>
                                    {selectedItems.length ? (
                                        <div className='moduleList_selectedItemsContainer'>
                                            <ComponentCheckbox
                                                customChecked={isMassCheckboxChecked}
                                                customHandler={handleMassCheckboxClick}
                                            />
                                            <span>
                                                    {intl.formatMessage({
                                                        id: 'LIST.SELECTED_ITEMS',
                                                    })}
                                                <b>{selectedItems.length}</b>
                                                </span>
                                            {groupUpdateFields ? (
                                                <ComponentButton
                                                    type='custom'
                                                    settings={{
                                                        title: intl.formatMessage({
                                                            id: 'LIST.MASS_EDIT_BUTTON',
                                                        }),
                                                        background: 'dark',
                                                        icon: 'edit',
                                                    }}
                                                    customHandler={() => {
                                                        setShowUpdateFieldsModal(true)
                                                        setFieldValue('mode', 'edit')
                                                    }}
                                                    defaultLabel='icon'
                                                />
                                            ) : null}
                                            <ComponentButton
                                                type='submit'
                                                settings={{
                                                    title: intl.formatMessage({
                                                        id: 'LIST.MASS_DELETE_BUTTON',
                                                    }),
                                                    background: 'danger',
                                                    icon: 'trash',
                                                    attention_modal: true,
                                                }}
                                                customHandler={handleSubmit}
                                                defaultLabel='icon'
                                            />
                                        </div>
                                    ) : null}
                                    { isMobile ? (
                                        <div className={`moduleList_cards ${selectedItems.length ? 'with-actions' : ''}`}>
                                            { !isLoaded ? (
                                               <SkeletonCards />
                                            ): isEmptyData ? (
                                                <div className="moduleList_emptyCards">
                                                    {intl.formatMessage({ id: 'LIST.EMPTY_ITEMS_LIST' })}
                                                </div>
                                                ) : Array.isArray(data)
                                                ? data.map((item) => {
                                                    return (
                                                        <ListCard
                                                            key={
                                                                item.id ??
                                                                item[
                                                                    resolvedHeaders[0].article
                                                                    ]
                                                            }
                                                            data={item}
                                                            headers={resolvedHeaders}
                                                            page={currentLink}
                                                            filterKeys={filterKeys}
                                                            isListEditable={isListEditable}
                                                            setFilter={setFilter}
                                                            setIndividualPage={
                                                                setIndividualPage
                                                            }
                                                            isNewUI={isNewUI}
                                                        />
                                                    )
                                                })
                                                : null}
                                        </div>
                                    ) : (
                                            <div
                                                className={
                                                    `table-responsive noselect ${
                                                        isSticky ? 'moduleList_tableScroller' : ''
                                                    } ` + `${selectedItems.length ? ' selectedItems' : ''}`
                                                }
                                                ref={tableRef}
                                            >
                                                    <table
                                                        className={`table ${
                                                            isNewUI
                                                                ? 'datatable datatable-bordered'
                                                                : 'table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'
                                                        }`}
                                                    >
                                                        <thead>
                                                        <tr className={isNewUI ? 'fw-bold text-muted' : ''}>
                                                            {isListEditable ? (
                                                                <th className='moduleList_headerCell deleteCheckbox'>
                                                                    <ComponentCheckbox
                                                                        customChecked={
                                                                            isMassCheckboxChecked
                                                                        }
                                                                        customHandler={
                                                                            handleMassCheckboxClick
                                                                        }
                                                                        is_disabled={
                                                                            isEmptyData ||
                                                                            !dataWithIds.length
                                                                        }
                                                                    />
                                                                </th>
                                                            ) : null}
                                                            {resolvedHeaders.map((column) => (
                                                                <HeaderCell
                                                                    key={column.article}
                                                                    article={column.article}
                                                                    title={column.title}
                                                                    type={column.type}
                                                                    filter={filter}
                                                                    setFilter={setFilter}
                                                                    isListEditable={isListEditable}
                                                                    isNewUI={isNewUI}
                                                                />
                                                            ))}
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {!isLoaded ? (
                                                            <SkeletonRows
                                                                headers={resolvedHeaders}
                                                                previousRowsCount={data?.length}
                                                                isListEditable={isListEditable}
                                                                isNewUI={isNewUI}
                                                            />
                                                        ) : null}
                                                        {isLoaded && Array.isArray(data)
                                                            ? data.map((item) => {
                                                                return (
                                                                    <ListRow
                                                                        /* в отчетных списках иногда отсутствую id. В качестве ключа - артикул первого же столбца */
                                                                        key={
                                                                            item.id ??
                                                                            item[
                                                                                resolvedHeaders[0].article
                                                                                ]
                                                                        }
                                                                        data={item}
                                                                        headers={resolvedHeaders}
                                                                        page={currentLink}
                                                                        filterKeys={filterKeys}
                                                                        isListEditable={isListEditable}
                                                                        setFilter={setFilter}
                                                                        setIndividualPage={
                                                                            setIndividualPage
                                                                        }
                                                                        isNewUI={isNewUI}
                                                                    />
                                                                )
                                                            })
                                                            : null}

                                                        {isLoaded && isEmptyData ? (
                                                            <tr>
                                                                <td
                                                                    className='moduleList_emptyTable'
                                                                    colSpan={
                                                                        resolvedHeaders.length
                                                                            ? resolvedHeaders.length + 1
                                                                            : 1
                                                                    }
                                                                >
                                                                    {intl.formatMessage({
                                                                        id: 'LIST.EMPTY_ITEMS_LIST',
                                                                    })}
                                                                </td>
                                                            </tr>
                                                        ) : null}
                                                        </tbody>
                                                    </table>
                                            </div>
                                        )
                                    }
                                    {isSticky && (
                                        <div className='moduleList_stickyHScroll' ref={fakeBarRef}>
                                            <div className='moduleList_stickyHScroll__inner' />
                                        </div>
                                    )}
                                    {isSetSearchData ? null : withInfiniteScroll ? (
                                        <InfiniteScroll
                                            fetch={fetchNextPage}
                                            currentRowsCount={data?.length}
                                            rowsCount={detail?.rows_count}
                                            hasNextPage={hasNextPage}
                                            isFetching={isInfiniteDataFetching}
                                        />
                                    ) : (
                                        <Pagination
                                            detail={detail}
                                            filter={filter}
                                            setFilter={setFilter}
                                            isNewUI={isNewUI}
                                            settingsLimit={limit}
                                        />
                                    )}

                                    <UpdateFieldsModal
                                        showModal={showUpdateFieldsModal}
                                        hideModal={setShowUpdateFieldsModal}
                                        fields={groupUpdateFields?.filter(
                                            (field: TModuleListUpdateField) =>
                                                field.is_in_mass_editing !== false
                                        )}
                                        selectedItems={selectedItems}
                                    />
                                </FormikForm>
                            )
                        }}
                    </Formik>
                </div>
            </div>
        </ModuleContext.Provider>
    )
})

export default ModuleList
